import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box, Button, Chip, IconButton, Stack, Switch, Tab, Tabs,
    TextField, Tooltip, Typography,
} from "@mui/material";
import SaveRoundedIcon           from "@mui/icons-material/SaveRounded";
import RefreshRoundedIcon        from "@mui/icons-material/RefreshRounded";
import { notify } from "../../../util/toast";
import LoadingDots from "../../../component/common/LoadingDots";
import { formAdminApi } from "./formAdminApi";
import FormListSidebar from "./FormListSidebar";
import FieldsEditor    from "./FieldsEditor";
import RulesEditor     from "./RulesEditor";
import PublishPanel    from "./PublishPanel";
import FormPreviewModal from "./FormPreviewModal";

/**
 * 결재 양식 관리 — 관리자가 양식을 직접 고치는 화면.
 *
 * <h3>저장과 발행이 다르다</h3>
 * 저장하면 <b>초안</b>이 생긴다. 초안은 상신 화면에 아무 영향이 없다 — 직원들은 여전히 예전 내용을 본다.
 * 발행해야 바뀐다. 그래서 저장만 하고 발행을 잊으면 "고쳤는데 왜 그대로지" 가 되므로,
 * 사이드바와 헤더 양쪽에 «초안» 배지를 띄운다.
 *
 * <h3>발행하면 새 버전이 생긴다</h3>
 * 진행 중인 문서는 자기 버전을 그대로 쓴다. 3단계로 올라간 문서를 2단계로 줄여도 그 문서는 3단계로 끝난다.
 */
export default function FormAdminPanel({ myInfo }) {
    const [forms, setForms]     = useState([]);
    const [dict, setDict]       = useState({ slots: [], capabilities: [] });
    const [selected, setSelected] = useState(null);
    const [tab, setTab]         = useState(0);

    const [fields, setFields]   = useState([]);
    const [rules, setRules]     = useState({ capabilities: [], line: [], viewers: [] });
    const [notice, setNotice]   = useState("");

    const [loading, setLoading]   = useState(true);
    const [loadingForm, setLoadingForm] = useState(false);
    const [saving, setSaving]     = useState(false);
    const [checking, setChecking] = useState(false);
    const [publishing, setPublishing] = useState(false);

    const [dirty, setDirty]     = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [versionId, setVersionId] = useState(null);
    const [check, setCheck]     = useState(null);
    const [preview, setPreview] = useState([]);

    /* 이번 편집에서 새로 만든 칸의 키 — 저장 키를 바꿔도 되는 칸인지 구분한다.
       기존 칸의 키를 바꾸면 이미 올라간 문서가 그 값을 못 찾으므로 경고를 띄운다. */
    const newKeys = useRef(new Set());

    useEffect(() => {
        Promise.all([formAdminApi.list(), formAdminApi.dictionary()])
            .then(([l, d]) => {
                setForms(Array.isArray(l) ? l : []);
                setDict(d || { slots: [], capabilities: [] });
            })
            .catch(() => notify("양식 목록을 불러오지 못했습니다.", "error"))
            .finally(() => setLoading(false));
    }, []);

    const openForm = async (f) => {
        if (dirty && !window.confirm("저장하지 않은 변경이 있습니다. 버리고 이동할까요?")) return;
        setLoadingForm(true);
        setSelected(f);
        setNotice(f.notice || "");
        setCheck(null);
        setPreview([]);
        setVersionId(null);
        setDirty(false);
        newKeys.current = new Set();
        try {
            const [sc, rl] = await Promise.all([
                formAdminApi.schema(f.id),
                formAdminApi.rules(f.id),
            ]);
            setFields(Array.isArray(sc) ? sc : []);
            setRules(rl || { capabilities: [], line: [], viewers: [] });
        } catch {
            notify("양식 정의를 불러오지 못했습니다.", "error");
            setFields([]);
        } finally {
            setLoadingForm(false);
        }
    };

    /** 저장 — 입력칸과 규칙을 같은 초안에 쌓는다. 반환된 versionId 로 점검·발행을 건다. */
    const save = async () => {
        if (!selected) return null;
        setSaving(true);
        try {
            await formAdminApi.saveRules(selected.id, rules);
            const res = await formAdminApi.saveSchema(selected.id, fields);
            if (notice !== (selected.notice || "")) {
                await formAdminApi.setNotice(selected.id, notice);
                setSelected((s) => ({ ...s, notice }));
                setForms((list) => list.map((f) => (f.id === selected.id ? { ...f, notice } : f)));
            }
            const vid = res?.versionId ?? null;
            setVersionId(vid);
            setDirty(false);
            setCheck(null);                    // 내용이 바뀌었으므로 지난 점검 결과는 못 쓴다
            setForms((list) => list.map((f) => (f.id === selected.id ? { ...f, hasDraft: true } : f)));
            notify("초안으로 저장했습니다. 발행해야 직원 화면에 반영됩니다.", "success");
            return vid;
        } catch (e) {
            notify(e?.response?.data?.message || "저장에 실패했습니다.", "error");
            return null;
        } finally {
            setSaving(false);
        }
    };

    /** 점검 — 항상 먼저 저장한다. 화면에 보이는 것과 점검 대상이 달라지면 결과를 믿을 수 없다. */
    const runCheck = async () => {
        const vid = dirty || !versionId ? await save() : versionId;
        if (!vid) return;
        setChecking(true);
        try {
            const [c, p] = await Promise.all([
                formAdminApi.check(vid),
                formAdminApi.preview(vid).catch(() => []),
            ]);
            setCheck(c);
            setPreview(Array.isArray(p) ? p : []);
        } catch (e) {
            notify(e?.response?.data?.message || "점검에 실패했습니다.", "error");
        } finally {
            setChecking(false);
        }
    };

    const doPublish = async () => {
        if (!versionId) return;
        const sideEffects = (rules.capabilities || [])
            .map((code) => dict.capabilities.find((c) => c.code === code))
            .filter((c) => c?.sideEffect);
        const msg = sideEffects.length
            ? `발행하면 이 양식은 승인 시 실제 데이터를 만듭니다.\n\n${
                sideEffects.map((c) => `· ${c.label}`).join("\n")}\n\n발행할까요?`
            : "이 버전을 발행할까요? 이후 상신부터 적용됩니다.";
        if (!window.confirm(msg)) return;

        setPublishing(true);
        try {
            await formAdminApi.publish(versionId);
            notify("발행했습니다. 이후 상신부터 적용됩니다.", "success");
            setForms((list) => list.map((f) => (f.id === selected.id ? { ...f, hasDraft: false } : f)));
            setVersionId(null);
            setCheck(null);
            await openForm({ ...selected, hasDraft: false });
        } catch (e) {
            notify(e?.response?.data?.message || "발행에 실패했습니다.", "error");
        } finally {
            setPublishing(false);
        }
    };

    const toggleActive = async () => {
        const next = !selected.active;
        if (!next && !window.confirm("사용을 중지하면 신규 상신 목록에서 빠집니다. (기존 문서 조회는 계속됩니다)")) return;
        try {
            await formAdminApi.setActive(selected.id, next);
            setSelected((s) => ({ ...s, active: next }));
            setForms((list) => list.map((f) => (f.id === selected.id ? { ...f, active: next } : f)));
        } catch {
            notify("변경에 실패했습니다.", "error");
        }
    };

    const edit = (setter) => (v) => { setter(v); setDirty(true); setCheck(null); };

    const slots = useMemo(() => dict.slots || [], [dict]);

    if (loading) return <Box sx={{ py: 8 }}><LoadingDots /></Box>;

    // 높이 차감분에 관리자 탭 막대(제목줄+탭+간격 ≈ 80px)가 포함돼 있다 —
    // 빼먹으면 좌측 양식 목록이 화면 아래로 밀려 매번 스크롤해야 한다.
    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 170px)" }}>
            {/* 헤더 — 화면 이름·나가기는 위쪽 관리자 탭(ApprovalAdminTabs)이 맡는다. */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <Box sx={{ flex: 1 }} />
                {selected && (
                    <>
                        {dirty && <Chip size="small" label="저장 안 됨"
                                        sx={{ height: 20, fontSize: 11, fontWeight: 800,
                                              bgcolor: "#fef3c7", color: "#b45309" }} />}
                        <Button size="small" onClick={() => setPreviewOpen(true)} sx={{ fontWeight: 700 }}>
                            미리보기
                        </Button>
                        <Button
                            size="small" variant="outlined" startIcon={<SaveRoundedIcon />}
                            onClick={save} disabled={saving}
                            sx={{ fontWeight: 700 }}
                        >
                            {saving ? "저장 중…" : "초안 저장"}
                        </Button>
                    </>
                )}
                <Tooltip title="목록 새로고침">
                    <IconButton size="small" onClick={() => formAdminApi.list().then(setForms)}>
                        <RefreshRoundedIcon sx={{ fontSize: 18, color: "#2563eb" }} />
                    </IconButton>
                </Tooltip>
            </Stack>

            <Box sx={{ flex: 1, display: "flex", border: "1px solid #e2e8f0",
                       borderRadius: 2, overflow: "hidden", minHeight: 0 }}>
                <FormListSidebar
                    forms={forms}
                    selectedId={selected?.id}
                    onSelect={openForm}
                    dirtyId={dirty ? selected?.id : null}
                />

                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
                    {!selected ? (
                        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                                   color: "#94a3b8", fontSize: 13 }}>
                            왼쪽에서 양식을 고르세요.
                        </Box>
                    ) : (
                        <>
                            {/* 양식 헤더 */}
                            <Box sx={{ px: 2, pt: 1.5, borderBottom: "1px solid #e2e8f0" }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#0f172a" }}>
                                        {selected.name}
                                    </Typography>
                                    <Chip size="small" label={selected.code}
                                          sx={{ height: 18, fontSize: 10, bgcolor: "#f1f5f9", color: "#64748b" }} />
                                    {selected.hasDraft && (
                                        <Chip size="small" label="미발행 초안 있음"
                                              sx={{ height: 18, fontSize: 10, fontWeight: 800,
                                                    bgcolor: "#fef3c7", color: "#b45309" }} />
                                    )}
                                    <Box sx={{ flex: 1 }} />
                                    <Typography sx={{ fontSize: 12, color: selected.active ? "#15803d" : "#94a3b8" }}>
                                        {selected.active ? "사용 중" : "사용 중지"}
                                    </Typography>
                                    <Switch size="small" checked={!!selected.active} onChange={toggleActive} />
                                </Stack>

                                <Tabs value={tab} onChange={(_, v) => setTab(v)}
                                      sx={{ minHeight: 36, "& .MuiTab-root": { minHeight: 36, fontSize: 13, fontWeight: 700 } }}>
                                    <Tab label={`입력칸 (${fields.length})`} />
                                    <Tab label="연동 · 결재선 · 열람자" />
                                    <Tab label="안내문구" />
                                    <Tab label="발행" />
                                </Tabs>
                            </Box>

                            <Box sx={{ flex: 1, overflowY: "auto", p: 2, minHeight: 0 }}>
                                {loadingForm ? <LoadingDots /> : (
                                    <>
                                        {tab === 0 && (
                                            <FieldsEditor
                                                fields={fields}
                                                slots={slots}
                                                newKeys={newKeys.current}
                                                onChange={edit(setFields)}
                                            />
                                        )}
                                        {tab === 1 && (
                                            <RulesEditor
                                                rules={rules}
                                                capabilities={dict.capabilities || []}
                                                onChange={edit(setRules)}
                                            />
                                        )}
                                        {tab === 2 && (
                                            <Box>
                                                <Typography sx={{ fontSize: 12, color: "#64748b", mb: 1 }}>
                                                    상신 화면에서 이 양식을 고르면 맨 위에 뜨는 문구입니다.
                                                </Typography>
                                                <TextField
                                                    multiline minRows={6} fullWidth value={notice}
                                                    onChange={(e) => { setNotice(e.target.value); setDirty(true); }}
                                                    placeholder="예) 세금계산서는 발행일 기준으로 작성해 주세요."
                                                />
                                            </Box>
                                        )}
                                        {tab === 3 && (
                                            <PublishPanel
                                                check={check}
                                                preview={preview}
                                                checking={checking || saving}
                                                publishing={publishing}
                                                onCheck={runCheck}
                                                onPublish={doPublish}
                                            />
                                        )}
                                    </>
                                )}
                            </Box>
                        </>
                    )}
                </Box>
            </Box>

            {selected && (
                <FormPreviewModal
                    open={previewOpen}
                    onClose={() => setPreviewOpen(false)}
                    form={selected}
                    fields={fields}
                    notice={notice}
                    myInfo={myInfo}
                />
            )}
        </Box>
    );
}
